import { IH2HStandingsData } from "core-integration/src/store/leagues/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import ButtonLink from "plos/src/components/links/ButtonLink";
import { Column, Table } from "plos/src/components/Table";
import { InfoText } from "plos/src/components/tooltips/InfoText";
import { fixedTable } from "plos/src/styles/utils.css";
import { TableBody, TableHeader } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { NextIcon, PrevIcon } from "../../../../icons/Chevrons";
import {
  getQueryParam,
  updateQueryParams,
} from "../../../Classic/StandingsClassic/utils";
import { leaguesTableWrapper, numericCol } from "../../../shared";
import {
  nextButton,
  pager,
  rankCol,
} from "../../../shared/styles/leaguesTableStyles.css";
import StandingsH2HRow from "./StandingsH2HRow";
import { teamAndManagerCol } from "./standingsH2HTable.css";

interface StandingsH2HTableProps {
  standings: IH2HStandingsData | null;
  eventId?: number;
}

const StandingsH2HTable = ({ standings, eventId }: StandingsH2HTableProps) => {
  const { t } = useTranslation("leaguesAndCups");
  const location = useLocation();

  const player = useSelector(getPlayerData);
  const pageStandings = getQueryParam({ location, key: "page_standings" });
  const pageStandingsAsNumber = pageStandings ? parseInt(pageStandings, 10) : 1;

  const previousLink = updateQueryParams({
    location,
    newParams: { page_standings: pageStandingsAsNumber - 1 },
  });
  const nextLink = updateQueryParams({
    location,
    newParams: { page_standings: pageStandingsAsNumber + 1 },
  });

  if (!eventId) return null;

  const { has_next: hasNext, results: standingsResults } = standings || {};

  return (
    <>
      <div className={leaguesTableWrapper}>
        <Table
          className={fixedTable}
          aria-label={t(
            "standingsH2HTable.ariaLabel",
            "Standings head to head",
          )}
        >
          <TableHeader>
            <Column className={rankCol}>
              {t("standingsH2HTable.rank", "Rank")}
            </Column>
            <Column isRowHeader className={teamAndManagerCol}>
              {t("standingsH2HTable.teamAndManager", "Team & Manager")}
            </Column>
            <Column className={numericCol}>
              <InfoText label={t("standingsH2HTable.won", "Won")}>
                {t("standingsH2HTable.wonShort", "W")}
              </InfoText>
            </Column>
            <Column className={numericCol}>
              <InfoText label={t("standingsH2HTable.drew", "Drew")}>
                {t("standingsH2HTable.drewShort", "D")}
              </InfoText>
            </Column>
            <Column className={numericCol}>
              <InfoText label={t("standingsH2HTable.lost", "Lost")}>
                {t("standingsH2HTable.lostShort", "L")}
              </InfoText>
            </Column>
            <Column className={numericCol}>
              <InfoText label={t("standingsH2HTable.score", "Score")}>
                {t("standingsH2HTable.score", "Score")}
              </InfoText>
            </Column>
            <Column className={numericCol}>
              <InfoText label={t("standingsH2HTable.points", "Points")}>
                {t("standingsH2HTable.pointsShort", "Pts")}
              </InfoText>
            </Column>
          </TableHeader>
          <TableBody
            renderEmptyState={() =>
              t(
                "standingsH2HTable.leaguesNotStarted",
                "Standings data will appear here when the league starts",
              )
            }
          >
            {standingsResults &&
              standingsResults?.length !== 0 &&
              standingsResults.map((ls, idx) => {
                const mine = player?.entry === ls.entry;
                const mineIdx = standingsResults.findIndex(
                  (ls) => player?.entry === ls.entry,
                );
                const isRowBeforeMine = idx === mineIdx - 1;
                const showBorder = !mine && !isRowBeforeMine;
                // eslint-disable-next-line i18next/no-literal-string
                const rowVariant = mine ? "mine" : "default";

                return (
                  <StandingsH2HRow
                    key={ls.entry}
                    rowData={ls}
                    eventId={eventId}
                    rowStyleVariant={rowVariant}
                    cellStyleVariants={{ mine, borderBottom: showBorder }}
                  />
                );
              })}
          </TableBody>
        </Table>
      </div>
      {/*  TODO update to new Pager Buttons */}
      <div className={pager}>
        {pageStandingsAsNumber > 1 && (
          <ButtonLink to={previousLink} appearance="tonal" size="small">
            <PrevIcon />
            <span>{t("pagination.previous", "Previous")}</span>
          </ButtonLink>
        )}
        {hasNext && (
          <ButtonLink
            to={nextLink}
            appearance="tonal"
            size="small"
            className={nextButton}
          >
            <span>{t("pagination.next", "Next")}</span>
            <NextIcon />
          </ButtonLink>
        )}
      </div>
    </>
  );
};

export default StandingsH2HTable;
